/** @jsxImportSource @emotion/react */
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import moment from 'moment/moment'
import { useEffect, useState } from 'react'
import Button from 'react-bootstrap/Button'
import { useParams } from 'react-router-dom'
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined'
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn'
import ReportIcon from '@mui/icons-material/Report'
import BlockIcon from '@mui/icons-material/Block'
import PrintIcon from '@mui/icons-material/Print'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import Select from 'react-select'

import {
  useDeviceLostReversionMutation,
  useHstDeviceReversionMutation,
  useLazyGetNightOwlShippingHistoryQuery,
  useRefundNightOwlShipmentMutation,
  useReshipThirdPartyDeviceMutation,
  useReturnThirdPartyDeviceMutation,
  useUpdateShippingStatusMutation,
} from 'store/services/labOrder'
import { DeviceRemovalReason, Order } from 'store/types'
import Toast from 'components/atoms/Toast'
import { confirm } from 'components/molecules/Confirmation'
import { isDevicePendingReturn } from 'helpers/orderStateHelpers'
import { RemoveDeviceModal } from '../RemoveDeviceModal'
import { showCheckInNotice } from '../../device/shelfLifeNotices'
import { CheckMeO2PpgUploadModal } from './CheckMeO2PpgUploadModal'
import { TestWebhookForm } from './TestWebhookForm'

interface IProps {
  order: Order
  refetchOrder?: () => void
}

interface TrackingHistoryItem {
  status_date: string
  status_details: string
  location?: {
    city?: string
    state?: string
  }
}

interface ShippingHistoryData {
  tracking_history?: TrackingHistoryItem[]
}

export const shippingStatusOptions = [
  { value: 'SHIPPED', label: 'Device Shipped' },
  { value: 'DELIVERED', label: 'Device Delivered' },
  { value: 'SHIPPED_BACK', label: 'Device Shipped to TestSmarter' },
]

const isTestSmarterDevice = (
  device: Order['thirdPartyDevice'],
): device is NonNullable<Order['thirdPartyDevice']> & {
  serial?: string
  trackingNumber?: string
  returnTrackingNumber?: string
  trackingUrlProvider?: string
  returnTrackingUrlProvider?: string
  shippedAt?: Date
  deliveredAt?: Date
  returnedAt?: Date
  returnShippedAt?: Date
  manualShipping?: boolean
  localPickup?: boolean
} => {
  // A Wesper dropship device has no serial (Wesper ships it directly, TestSmarter never
  // assigns one) but does carry tracking fields written by the backend's
  // onWesperOrderShippedEvent reducer — accept either shape.
  return (
    device !== null &&
    device !== undefined &&
    ('serial' in device || 'trackingNumber' in device)
  )
}

const ShippingSection = ({ order, refetchOrder }: IProps) => {
  const { orderId } = useParams()
  const [shippingStatus, setShippingStatus] = useState({
    value: 'SHIPPED',
    label: 'Device Shipped',
  })
  const [refundShipment] = useRefundNightOwlShipmentMutation()
  const [reshipThirdPartyDevice] = useReshipThirdPartyDeviceMutation()
  const [returnThirdPartyDevice] = useReturnThirdPartyDeviceMutation()
  const [updateShippingStatus] = useUpdateShippingStatusMutation()
  const [hstDeviceReversion] = useHstDeviceReversionMutation()
  const [deviceLostReversion] = useDeviceLostReversionMutation()
  const [getShippingHistory, { currentData: shippingHistory }] =
    useLazyGetNightOwlShippingHistoryQuery()

  // Modal state
  const [isRemoveDeviceModalOpen, setIsRemoveDeviceModalOpen] = useState(false)
  const [removeDeviceType, setRemoveDeviceType] = useState<'HST' | 'POX'>('HST')
  const [isRemovingDevice, setIsRemovingDevice] = useState(false)
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
  const isCheckMeO2Ppg = order.device?.model === 'checkmeo2-ppg'

  const env = process.env.REACT_APP_ENV ?? 'development'
  const showTestWebhookForm = env === 'local' || env === 'development'

  const deviceShippedBack = isTestSmarterDevice(order.thirdPartyDevice)
    ? !!order.thirdPartyDevice.returnShippedAt
    : false
  const devicePendingReturn = isDevicePendingReturn(order)

  const deviceShipped = isTestSmarterDevice(order.thirdPartyDevice)
    ? !!order.thirdPartyDevice.shippedAt
    : false
  const deviceDelivered = isTestSmarterDevice(order.thirdPartyDevice)
    ? !!order.thirdPartyDevice.deliveredAt
    : false
  const deviceReturned = isTestSmarterDevice(order.thirdPartyDevice)
    ? !!order.thirdPartyDevice.returnedAt
    : false
  
  const deviceSerialAssigned = isTestSmarterDevice(order.thirdPartyDevice)
    ? !!order.thirdPartyDevice.serial
    : false

  const manualShipping = isTestSmarterDevice(order.thirdPartyDevice)
    ? !!order.thirdPartyDevice.manualShipping
    : false
  const localPickup = isTestSmarterDevice(order.thirdPartyDevice)
    ? !!order.thirdPartyDevice.localPickup
    : false
  // Wesper's SHIPPED webhook also sets manualShipping=true (TestSmarter didn't buy the
  // label), so the badge below needs to tell that apart from staff-entered manual tracking.
  // Label the shipment as Wesper-fulfilled only when Wesper actually reported it.
  // A Wesper dispense order we ship ourselves can also carry a staff-entered tracking
  // number, and that is ordinary manual shipping. So is an inventory device that replaced a
  // returned dropship — Wesper's tracking stays on wesperInfo but describes the old kit.
  const isWesperShipped = !!order.wesperInfo?.trackingCode && !order.deviceId

  useEffect(() => {
    if (!localPickup && deviceShipped && orderId) {
      getShippingHistory({ orderId: Number(orderId) })
    }
  }, [deviceShipped, orderId, getShippingHistory])

  // Modal handlers
  const handleRemoveDeviceClick = (deviceType: 'HST' | 'POX') => {
    setRemoveDeviceType(deviceType)
    setIsRemoveDeviceModalOpen(true)
  }

  const handleRemoveDeviceConfirm = async (reason: DeviceRemovalReason) => {
    if (!orderId) return

    setIsRemovingDevice(true)
    const checkedIn = order.device
    try {
      if (removeDeviceType === 'HST') {
        await hstDeviceReversion({
          orderId: Number(orderId),
          reason: reason,
        }).unwrap()

        if (refetchOrder) {
          refetchOrder()
        }

        Toast({
          type: 'success',
          label: 'HST device removed from order. Order moved back to Patient Scheduling bucket.',
        })
      } else {
        await deviceLostReversion({
          orderId: Number(orderId),
          reason: reason,
        }).unwrap()

        Toast({
          type: 'success',
          label: 'Device removed from order. Order moved to Follow Up bucket.',
        })
      }

      // a device that was not used has not worn its accessories
      void showCheckInNotice(checkedIn, { includeAccessories: reason !== DeviceRemovalReason.NOT_USED })
    } catch (error: any) {
      Toast({
        type: 'error',
        label: `Failed to remove device from order: ${error?.data?.message || error?.message || 'Unknown error'}`,
      })
    } finally {
      setIsRemovingDevice(false)
      setIsRemoveDeviceModalOpen(false)
    }
  }

  const handleRemoveDeviceCancel = () => {
    setIsRemoveDeviceModalOpen(false)
  }


  return (
    <div>
      <div className="mt-3 d-flex">
        {devicePendingReturn ? (
          <>
            <ErrorOutlineIcon className={'mr-2 text-danger'} />
            <h5 className={'font-weight-600'}>
              Device has not yet been {localPickup ? 'Returned' : 'Shipped'} to TestSmarter
            </h5>
          </>
        ) : deviceReturned ? (
          <>
            <CheckBoxOutlinedIcon className={'mr-2 text-success'} />
            <h5 className={'font-weight-600'}>Device Returned to TestSmarter</h5>
          </>
        ) : deviceShippedBack ? (
          <>
            <CheckBoxOutlinedIcon className={'mr-2 text-success'} />
            <h5 className={'font-weight-600'}>Device Shipped to TestSmarter</h5>
          </>
        ) : deviceDelivered ? (
          <>
            <CheckBoxOutlinedIcon className={'mr-2 text-success'} />
            <h5 className={'font-weight-600'}>Device Delivered to Patient</h5>
          </>
        ) : deviceShipped ? (
          <>
            <CheckBoxOutlinedIcon className={'mr-2 text-success'} />
            <h5 className={'font-weight-600'}>Device Shipped to Patient</h5>
          </>
        ) : (
          <>
            <ErrorOutlineIcon className={'mr-2 text-danger'} />
            <h5 className={'font-weight-600'}>Device has not yet been Shipped to Patient</h5>
          </>
        )}
        {(manualShipping || localPickup) && (
          <span
            css={{
              marginLeft: 10,
              fontSize: 18,
              color: '#969696',
            }}>
            {manualShipping
              ? isWesperShipped
                ? '(Wesper Fulfilled)'
                : '(Manual Shipping)'
              : '(Local Pickup)'}
          </span>
        )}
      </div>
      <div className={'d-flex flex-column'}>
        <span>
          Device Serial #:{' '}
          <span className={'text-bold'}>
            {isTestSmarterDevice(order.thirdPartyDevice) ? order.thirdPartyDevice.serial : '-'}
          </span>
        </span>
        {!localPickup && (
          <>
            <span className={'mt-2'}>
              Tracking Number:{' '}
              <span className={'text-bold'}>
                {isTestSmarterDevice(order.thirdPartyDevice)
                  ? order.thirdPartyDevice.trackingNumber || '-'
                  : '-'}
              </span>
              {isTestSmarterDevice(order.thirdPartyDevice) &&
                order.thirdPartyDevice.trackingNumber &&
                (order.thirdPartyDevice.trackingUrlProvider ? (
                  <a
                    href={order.thirdPartyDevice.trackingUrlProvider}
                    target="_blank"
                    rel="noreferrer"
                    className={'text-blue ml-2'}>
                    Track Online
                  </a>
                ) : (
                  <span className={'text-muted ml-2'}>Track Online</span>
                ))}
            </span>
            <span>
              Return Tracking Number:{' '}
              <span className={'text-bold'}>
                {isTestSmarterDevice(order.thirdPartyDevice)
                  ? order.thirdPartyDevice.returnTrackingNumber || '-'
                  : '-'}
              </span>
              {isTestSmarterDevice(order.thirdPartyDevice) &&
                order.thirdPartyDevice.returnTrackingNumber &&
                (order.thirdPartyDevice.returnTrackingUrlProvider ? (
                  <a
                    href={order.thirdPartyDevice.returnTrackingUrlProvider}
                    target="_blank"
                    rel="noreferrer"
                    className={'text-blue ml-2'}>
                    Track Online
                  </a>
                ) : (
                  <span className={'text-muted ml-2'}>Track Online</span>
                ))}
            </span>
          </>
        )}
      </div>
      <div className={'d-flex mt-3'}>
        <Button
          variant="primary"
          className={'d-flex align-items-center'}
          disabled={
            !(
              isTestSmarterDevice(order.thirdPartyDevice) && order.thirdPartyDevice.trackingNumber
            ) || manualShipping
          }
          onClick={() => {
            const file = Object.values(order.files).find(
              (x) => x.filename === 'shippo-shipping-labels.pdf',
            )

            if (file) {
              window.open(file.url, '_blank')
            }
          }}>
          <PrintIcon className={'text-normal mr-2'} /> Print Shipping Labels
        </Button>
        {/* <Button
          variant={deviceDelivered ? 'success' : 'danger'}
          className="ml-2 d-flex align-items-center"
          disabled={!(isTestSmarterDevice(order.thirdPartyDevice) && order.thirdPartyDevice.trackingNumber) || deviceReturned}
          onClick={() =>
            confirm('', {
              title: 'Are you sure?',
              description: deviceDelivered
                ? 'This will mark the device as returned. Proceed?'
                : 'This will cancel the shipment and request a refund. Proceed?',
              yesAction: 'Yes, proceed',
              noAction: 'No, cancel',
            })
              .then(async () => {
                if (deviceDelivered) {
                  await returnThirdPartyDevice({ orderId: Number(orderId) }).unwrap()
                } else {
                  await refundShipment({ orderId: Number(orderId) }).unwrap()
                }
                Toast({
                  type: 'success',
                  label: deviceDelivered ? 'Device marked as returned' : 'Shipping cancelled',
                })
              })
              .catch(() =>
                Toast({
                  type: 'error',
                  label: 'The request encountered a problem. Please try again or contact support.',
                }),
              )
          }>
          {deviceDelivered ? (
            <>
              <KeyboardReturnIcon className={'text-normal mr-2'} /> Return Device
            </>
          ) : (
            <>
              <BlockIcon className={'text-normal mr-2'} /> Cancel Shipment & Request Refund
            </>
          )}
        </Button> */}
        {!deviceDelivered && !manualShipping && (
          <Button
            variant={'danger'}
            className="ml-2 d-flex align-items-center"
            disabled={
              !(
                isTestSmarterDevice(order.thirdPartyDevice) && order.thirdPartyDevice.trackingNumber
              ) || deviceReturned
            }
            onClick={() =>
              confirm('', {
                title: 'Are you sure?',
                description: 'This will cancel the shipment and request a refund. Proceed?',
                yesAction: 'Yes, proceed',
                noAction: 'No, cancel',
              })
                .then(async () => {
                  await refundShipment({ orderId: Number(orderId) }).unwrap()
                  Toast({
                    type: 'success',
                    label: 'Shipping cancelled',
                  })
                })
                .catch(() => {})
            }>
            <BlockIcon className={'text-normal mr-2'} /> Cancel Shipment & Request Refund
          </Button>
        )}
        <Button
          variant={'success'}
          className="ml-2 d-flex align-items-center"
          onClick={() =>
            confirm('', {
              title: 'Are you sure?',
              description: 'This will mark the device as returned. Proceed?',
              yesAction: 'Yes, proceed',
              noAction: 'No, cancel',
            })
              .then(async () => {
                const checkedIn = order.device
                await returnThirdPartyDevice({ orderId: Number(orderId) }).unwrap()
                Toast({
                  type: 'success',
                  label: 'Device marked as returned',
                })
                void showCheckInNotice(checkedIn)
              })
              .catch(() => {})
          }>
          <KeyboardReturnIcon className={'text-normal mr-2'} /> Return Device
        </Button>
        {isCheckMeO2Ppg && (
          <Button
            variant={'primary'}
            className="ml-2 d-flex align-items-center"
            disabled={!deviceShippedBack}
            title={
              deviceShippedBack
                ? undefined
                : 'Available once the device has been shipped back to TestSmarter.'
            }
            onClick={() => setIsUploadModalOpen(true)}>
            <CloudUploadIcon className={'text-normal mr-2'} /> Upload data
          </Button>
        )}
        {(order?.testType === 'HST' || order?.testType === 'POX') && deviceReturned && deviceSerialAssigned && (
          <Button
            variant={'primary'}
            className="ml-2 d-flex align-items-center"
            onClick={() =>
              confirm('', {
                title: 'Reship Device?',
                description: 'This will reset the device assignment and move the order back to Need to Ship bucket, where you can assign a new device and generate a new shipping label. Proceed?',
                yesAction: 'Yes, reship',
                noAction: 'Cancel',
              })
                .then(async () => {
                  await reshipThirdPartyDevice({ 
                    orderId: Number(orderId),
                    reason: 'Report failed - reshipping required'
                  }).unwrap()
                  Toast({
                    type: 'success',
                    label: 'Order reset for reshipping. Please assign a new device.',
                  })
                  if (refetchOrder) {
                    refetchOrder()
                  }
                })
                .catch(() => {})
            }>
            <LocalShippingIcon className={'text-normal mr-2'} /> Reship Device
          </Button>
        )}
        {/* HST Remove Device Button */}
        {order?.testType === 'HST' &&
          isTestSmarterDevice(order.thirdPartyDevice) &&
          order.thirdPartyDevice.serial && (
            <Button
              variant={'warning'}
              className="ml-2 d-flex align-items-center"
              onClick={() => handleRemoveDeviceClick('HST')}
            >
              <ReportIcon className={'text-normal mr-2'} /> Remove HST Device
            </Button>
          )}
        {/* POX Remove Device Button */}
        {order?.testType !== 'HST' &&
          isTestSmarterDevice(order.thirdPartyDevice) &&
          order.thirdPartyDevice?.serial && (
            <Button
              variant={'warning'}
              className="ml-2 d-flex align-items-center"
              onClick={() => handleRemoveDeviceClick('POX')}
            >
              <ReportIcon className={'text-normal mr-2'} /> Remove POX Device
            </Button>
          )}
      </div>
      {!localPickup && (
        <>
          <h5 className={'mt-4 mb-2'}>Shipping Updates</h5>
          {!!shippingHistory ? (
            <ul className={'pl-0'}>
              {(shippingHistory as ShippingHistoryData)?.tracking_history?.map(
                (item: TrackingHistoryItem, index: number) => (
                  <li key={index}>
                    <strong css={{ minWidth: 100 }}>
                      {moment(item.status_date).format('MM/DD/YYYY hh:mm a')}
                    </strong>
                    : {item.status_details} ({item?.location?.city}
                    {''}
                    {item?.location?.state ? `, ${item?.location?.state}` : ''})
                  </li>
                ),
              )}
            </ul>
          ) : (
            'Tracking information will be displayed below as it becomes available.'
          )}
        </>
      )}
      <div className="d-flex align-items-center mt-4">
        <Select
          className="basic-single mb-2"
          classNamePrefix="select"
          isSearchable
          placeholder="Select shipping status"
          name="shipping_status"
          onChange={(item) => {
            if (!item) {
              return
            }

            setShippingStatus(item)
          }}
          options={shippingStatusOptions}
          value={shippingStatus}
          css={{
            width: 300,
            marginBottom: '0 !important',
          }}
        />
        <Button
          variant="outline-primary"
          onClick={async () => {
            await updateShippingStatus({
              orderId: Number(order.id),
              status: shippingStatus.value,
            })
          }}
          style={{
            marginLeft: 10,
          }}>
          Set shipping status
        </Button>
      </div>

      {/* Test Webhook Form (Dev/Local Only) */}
      {showTestWebhookForm && <TestWebhookForm refetchOrder={refetchOrder} />}

      {/* Remove Device Modal */}
      <RemoveDeviceModal
        isOpen={isRemoveDeviceModalOpen}
        onClose={handleRemoveDeviceCancel}
        onConfirm={handleRemoveDeviceConfirm}
        deviceType={removeDeviceType}
        orderId={orderId || ''}
        isLoading={isRemovingDevice}
      />

      {/* CheckMe O2 (PPG) device-data upload */}
      {isCheckMeO2Ppg && (
        <CheckMeO2PpgUploadModal
          isOpen={isUploadModalOpen}
          onClose={() => setIsUploadModalOpen(false)}
          order={order}
          refetchOrder={refetchOrder}
        />
      )}
    </div>
  )
}

export default ShippingSection
